import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import LoginPage from "./pages/LoginPage";
import ErrorPage from "./pages/ErrorPage";
import HomePage from "./pages/HomePage";
import Footer from "./components/Footer";
import SideBar from "./components/SideBar";
import Notes from "pages/Notes/Notes";
import useCustomTypography from "resources/useCustomTypography";
import FormsConfiguration from "pages/FormsConfiguration/FormsConfiguration";
import PatientsManagement from "pages/PatientsManagement/PatientsManagement";
import ProfessionalsManagement from "pages/ProfessionalsManagement/ProfessionalsManagement";
import Messages from "pages/Messages/Messages";
import DeleteMessageDialog from "pages/Messages/DeleteMessageDialog";
import DeleteNoteDialog from "pages/Notes/DeleteNoteDialog";
import Content from "pages/Content/ContentIndex";
import DeleteContentDialog from "pages/Content/Management/DeleteContentDialog";
import AssociateContentForm from "pages/Content/Management/AssociateContentForm";
import DuplicateContentForm from "pages/Content/Management/DuplicateContentForm";
import DeletePatientContentDialog from "pages/Content/Patients/DeletePatientContentDialog";
import PatientContent from "pages/Content/Patients/PatientContent";
import CentersManagement from "pages/Centers/CentersManagement";
import DeleteCenterDialog from "pages/Centers/DeleteCenterDialog";
import StudyDashboard from "pages/Study/StudyDashboard";
import { Alerts } from "pages/Alerts/Alerts";
import { DeleteAlertDialog } from "pages/Alerts/DeleteAlertDialog";
import Notification from "components/Notification";
import { createContent, updateContentAction, deleteContentAction } from "redux/reducers/contentReducer";
import DisplayContentManagement from "pages/Content/Management/DisplayContentManagement";
import { useDispatch, useSelector } from "react-redux";
import { keepUserSessionAlive } from "redux/reducers/userAuthenticationReducer";
import { LocalStorageManager } from "utils/LocalStorageManager";
import { authConstants } from "constants/authConstants";
import { RolesManager } from "utils/RolesManager";
import { DisplayNewCenterForm } from "pages/Centers/DisplayNewCenterForm";
import { DisplayEditCenterForm } from "pages/Centers/DisplayEditCenterForm";
import { DisplayEditSelectedCenterDetails } from "pages/Centers/DisplayEditSelectedCenterDetails";
import { initializeCenters } from "redux/reducers/centersReducer";
import ContentForm from "pages/Content/Management/ContentForm";
import NoteForm from "pages/Notes/NoteForm";
import { createNote, updateNoteAction } from "redux/reducers/notesReducer";
import MessageForm from "pages/Messages/MessageForm";
import { createMessage, updateMessageAction } from "redux/reducers/messagesReducer";
import { PackagesTable } from "pages/Packages/PackagesTable";
import PackageForm from "pages/Packages/PackageForm";
import { createBusinessPackage, updateBusinessPackageAction } from "redux/reducers/packagesReducer";
import ScheduledFormsConfiguration from "pages/ScheduledForms/ScheduledFormsConfiguration";
import { OrganizationsTable } from "pages/Organizations/OrganizationsTable";
import OrganizationForm from "pages/Organizations/OrganizationForm";
import DeleteOrganizationDialog from "pages/Organizations/DeleteOrganizationDialog";
import { createOrganization, updateOrganizationsAction } from "redux/reducers/organizationsReducer";

function PrivateRoute({ redirectPath = '/login' }) {
  const { user } = useSelector(state => state.userAuthentication)

  const dispatch = useDispatch()

  useEffect(() => {
    if (user?.roles && RolesManager.userIsSuperAdmin(user.roles)) {
      dispatch(initializeCenters());
    }
  }, [])

  if (!user) {
    return <Navigate to={redirectPath} replace />
  }
  // ** Outlet: This element is used as a placeholder for the child route.
  return (
    <SideBar>
      <Outlet />
    </SideBar>

  )
}

function ContentCreatorRoute({ redirectPath = '/login' }) {
  const { user } = useSelector(state => state.userAuthentication)

  if (!RolesManager.userIsContentCreator(user.roles)) {
    return <Navigate to={redirectPath} replace />
  }
  return <Outlet />
}

function AdminRoute({ redirectPath = '/contingut/management' }) {
  const { user } = useSelector(state => state.userAuthentication)

  if (!RolesManager.userIsAdmin(user.roles)) {
    return <Navigate to={redirectPath} replace />
  }
  return <Outlet />
}

function ProfessionalRoute({ redirectPath = '/contingut/management' }) {
  const { user } = useSelector(state => state.userAuthentication)

  if (!RolesManager.userIsProfessional(user.roles)) {
    return <Navigate to={redirectPath} replace />
  }
  return <Outlet />
}

function SuperAdminRoute({ redirectPath = '/contingut/management' }) {
  const { user } = useSelector(state => state.userAuthentication)

  if (!RolesManager.userIsSuperAdmin(user.roles)) {
    return <Navigate to={redirectPath} replace />
  }

  return <Outlet />
}

const App = () => {
  const { isAuthenticated } = useSelector(state => state.userAuthentication)
  const { getLocalStorageItem } = LocalStorageManager

  const dispatch = useDispatch()

  useEffect(() => {
    const userInStorage = getLocalStorageItem(authConstants.MonitoringUserNew)

    if (!isAuthenticated && userInStorage) {
      dispatch(keepUserSessionAlive(userInStorage))
    }
  }, [getLocalStorageItem, isAuthenticated, dispatch])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={useCustomTypography}>
        <Router>
          <Routes>
            <Route path="login" element={<LoginPage />} />
            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="*" element={<ErrorPage message={"404 - Not Found!"} />} />
            <Route element={<PrivateRoute />}>
              <Route element={<ContentCreatorRoute />}>
                <Route path="contingut" element={<Content />}>
                  <Route path="management" element={<DisplayContentManagement />}>
                    <Route path="create" element={<ContentForm onSubmit={createContent} />} />
                    <Route path=":id/edit" element={<ContentForm onSubmit={updateContentAction} isEdit />} />
                    <Route path=":id/delete" element={<DeleteContentDialog deleteContent={deleteContentAction} />} />
                    <Route path=":id/associate" element={<AssociateContentForm />} />
                    <Route path=":id/duplicate" element={<DuplicateContentForm updateContent={updateContentAction} />} />
                  </Route>
                  <Route path="patients" element={<PatientContent />}>
                    <Route path=":id/delete" element={<DeletePatientContentDialog />} />
                    <Route path=":id/info" element={<ContentForm isParticipantContent />} />
                  </Route>
                </Route>
              </Route>
              <Route element={<ProfessionalRoute />}>
              <Route path="dades-del-participant" element={<HomePage />} >
                  <Route path="contingut" element={<Content isDashboardView/>}>
                    <Route path="patients" element={<PatientContent />}>
                      <Route path=":id/delete" element={<DeletePatientContentDialog isDashboardView/>} />
                      <Route path=":id/info" element={<ContentForm isParticipantContent showInfoInDashBoard/>} />
                    </Route>
                  </Route>
                </Route>
                <Route path="notes" element={<Notes />}>
                  <Route path="create" element={<NoteForm onSubmit={createNote} />} />
                  <Route path=":id/edit" element={<NoteForm onSubmit={updateNoteAction} isEdit />} />
                  <Route path=":id/delete" element={<DeleteNoteDialog />} />
                </Route>
                <Route path="alertes" element={<Alerts />}>
                  <Route path=":id/delete" element={<DeleteAlertDialog />} />
                </Route>
                <Route path="missatges" element={<Messages />}>
                  <Route path="create" element={<MessageForm onSubmit={createMessage} />} />
                  <Route path=":id/edit" element={<MessageForm onSubmit={updateMessageAction} isEdit={true}/>} />
                  <Route path=":id/delete" element={<DeleteMessageDialog />} />
                </Route>
                <Route path="gestio-dels-pacients" element={<PatientsManagement />} />
                <Route path="patients-management" element={<PatientsManagement />} />
                <Route path="patients" element={<PatientContent />}>
                  <Route path=":id/delete" element={<DeletePatientContentDialog />} />
                </Route>
                <Route path="study" element={<StudyDashboard />} />
              </Route>
              <Route element={<AdminRoute />}>
                <Route path="gestio-de-professionals" element={<ProfessionalsManagement />} />
                <Route path="gestio-dels-pacients" element={<PatientsManagement />} />
                <Route path="configuracio-formularis" element={<FormsConfiguration />} />
                <Route path="patients-management" element={<PatientsManagement />} />
                <Route path="gestio-dels-centres/:centerId/edit" element={<DisplayEditCenterForm />} />
                <Route path="gestio-dels-centres/:id/fichas-de-centro" element={<DisplayEditSelectedCenterDetails />} />
              </Route>
              <Route element={<SuperAdminRoute />}>
                <Route path="gestio-dels-centres" element={<CentersManagement />}>
                  <Route path="create" element={<DisplayNewCenterForm />} />
                  <Route path=":id/delete" element={<DeleteCenterDialog />} />
                </Route>
                <Route path="programar-formularis" element={<ScheduledFormsConfiguration />} />
                <Route path="paquetes" element={<PackagesTable />} >
                  <Route path="create" element={<PackageForm onSubmit={createBusinessPackage}/>} />
                  <Route path=":id/edit" element={<PackageForm  onSubmit={updateBusinessPackageAction} isEdit/>} />
                </Route>
                <Route path="organizaciones" element={<OrganizationsTable />} >
                  <Route path="create" element={<OrganizationForm onSubmit={createOrganization}/>} /> 
                  <Route path=":id/edit" element={<OrganizationForm onSubmit={updateOrganizationsAction} isEdit />} />
                  <Route path=":id/delete" element={<DeleteOrganizationDialog />} />
                </Route>
              </Route>
            </Route>
          </Routes>
          <Notification />
          <Footer />
        </Router>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
